// ══════════════════════════════════════════════════════════════════════════════
// COLLAB PAGE - Page de collaboration avec externes (clients, partenaires)
// Style Workplace by Meta
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Settings, Search, MoreHorizontal, 
  UserPlus, Mail, Check, X, Crown, Eye, Edit3,
  MessageSquare, Image, Calendar, BarChart3, FileText,
  Send, Trash2, ChevronRight, ExternalLink, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import WorkplaceFeed from '../feed/WorkplaceFeed';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function CollabPage({ token: propToken, currentUser: propUser }) {
  const navigate = useNavigate();
  
  // Récupérer token et user depuis localStorage si pas fournis via props
  // Compatible avec le système d'auth du Cockpit Pro
  const [token] = useState(() => {
    if (propToken) return propToken;
    const storedToken = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token') || localStorage.getItem('token');
    // Mode démo si aucun token disponible
    return storedToken || 'demo_pro_token';
  });
  const [currentUser, setCurrentUser] = useState(propUser || null);
  
  const [collabs, setCollabs] = useState([]);
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMembersPanel, setShowMembersPanel] = useState(false);

  const headers = { 
    'Authorization': `Bearer ${token}`, 
    'Content-Type': 'application/json' 
  };
  
  // Charger l'utilisateur courant si pas fourni via props
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (propUser || currentUser) return;
      
      // D'abord essayer de récupérer depuis localStorage
      const storedUser = localStorage.getItem('ecosystem_user') || localStorage.getItem('client_user') || localStorage.getItem('user');
      if (storedUser) {
        try {
          setCurrentUser(JSON.parse(storedUser));
          return;
        } catch (e) { /* ignore */ }
      }
      
      // Sinon, essayer l'API
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, { headers });
        if (res.ok) {
          const data = await res.json();
          setCurrentUser(data);
        }
      } catch (error) {
        console.error('Erreur user:', error);
      }
    };
    if (token) fetchCurrentUser();
  }, [token, propUser, currentUser]);

  // Charger les collabs
  const fetchCollabs = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/collabs`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCollabs(data.collabs || []);
        if (data.collabs?.length > 0 && !selectedCollab) {
          setSelectedCollab(data.collabs[0]);
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Charger les membres
  const fetchMembers = useCallback(async (collabId) => {
    try {
      const res = await fetch(`${API_URL}/api/collabs/${collabId}/members`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Erreur membres:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchCollabs();
  }, [fetchCollabs]);

  useEffect(() => {
    if (selectedCollab) {
      fetchMembers(selectedCollab.id);
    }
  }, [selectedCollab, fetchMembers]);

  // Créer une collab
  const handleCreateCollab = async (name, description, color) => {
    try {
      const res = await fetch(`${API_URL}/api/collabs`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, description, color })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Collaboration créée !');
        setCollabs(prev => [data.collab, ...prev]);
        setSelectedCollab(data.collab);
        setShowCreateModal(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  // Inviter un membre
  const handleInvite = async (type, data) => {
    const endpoint = type === 'internal' 
      ? `${API_URL}/api/collabs/${selectedCollab.id}/members/internal`
      : `${API_URL}/api/collabs/${selectedCollab.id}/members/external`;
    
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (res.ok) {
        const result = await res.json();
        
        // Message de succès avec indication si l'email a été envoyé
        if (result.email_sent) {
          toast.success(`📧 Email d'invitation envoyé à ${data.email} !`);
        } else {
          toast.success(result.message);
        }
        
        fetchMembers(selectedCollab.id);
        setShowInviteModal(false);
        
        // Copier le lien d'invitation (avec gestion d'erreur silencieuse)
        if (result.invite_link) {
          try {
            await navigator.clipboard.writeText(result.invite_link);
            toast.info('Lien d\'invitation copié dans le presse-papier');
          } catch (clipboardError) {
            // Afficher le lien si le clipboard n'est pas disponible
            console.log('Lien d\'invitation:', result.invite_link);
          }
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de l\'invitation');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'invitation');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F1F5F9' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* SIDEBAR - Liste des Collabs */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <button 
            onClick={() => navigate('/cockpit-pro')}
            style={styles.backButton}
            data-testid="collab-back-btn"
          >
            <ArrowLeft size={18} />
          </button>
          <h2 style={styles.sidebarTitle}>
            <Users size={20} />
            Mes Collabs
          </h2>
          <button 
            style={styles.addButton}
            onClick={() => setShowCreateModal(true)}
            data-testid="collab-create-btn"
          >
            <Plus size={18} />
          </button>
        </div>

        <div style={styles.searchBox}>
          <Search size={16} style={{ color: '#94A3B8' }} />
          <input 
            type="text" 
            placeholder="Rechercher..." 
            style={styles.searchInput}
          />
        </div>

        <div style={styles.collabList}>
          {collabs.length === 0 ? (
            <div style={styles.emptyState}>
              <Users size={40} style={{ color: '#CBD5E1', marginBottom: 12 }} />
              <p>Aucune collaboration</p>
              <button 
                style={styles.createFirstButton}
                onClick={() => setShowCreateModal(true)}
              >
                Créer ma première Collab
              </button>
            </div>
          ) : (
            collabs.map(collab => (
              <div
                key={collab.id}
                style={{
                  ...styles.collabItem,
                  ...(selectedCollab?.id === collab.id ? styles.collabItemActive : {})
                }}
                onClick={() => setSelectedCollab(collab)}
              >
                <div 
                  style={{ 
                    ...styles.collabAvatar, 
                    background: collab.color || '#F97316' 
                  }}
                >
                  {collab.name.charAt(0).toUpperCase()}
                </div>
                <div style={styles.collabInfo}>
                  <div style={styles.collabName}>{collab.name}</div>
                  <div style={styles.collabMeta}>
                    {collab.member_count} membre{collab.member_count > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main style={styles.main}>
        {selectedCollab ? (
          <>
            {/* Header de la Collab */}
            <header style={styles.collabHeader}>
              <div style={styles.collabHeaderLeft}>
                <div 
                  style={{ 
                    ...styles.collabAvatarLarge, 
                    background: selectedCollab.color || '#F97316' 
                  }}
                >
                  {selectedCollab.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h1 style={styles.collabHeaderTitle}>{selectedCollab.name}</h1>
                  <p style={styles.collabHeaderDesc}>
                    {selectedCollab.description || 'Collaboration avec votre équipe et vos partenaires'}
                  </p>
                </div>
              </div>
              <div style={styles.collabHeaderActions}>
                <button 
                  style={styles.headerButton}
                  onClick={() => setShowMembersPanel(!showMembersPanel)}
                >
                  <Users size={18} />
                  {members.length} membres
                </button>
                <button 
                  style={styles.headerButtonPrimary}
                  onClick={() => setShowInviteModal(true)}
                >
                  <UserPlus size={18} />
                  Inviter
                </button>
                <button style={styles.headerButtonIcon}>
                  <Settings size={18} />
                </button>
              </div>
            </header>

            {/* Contenu: Feed + Panel Membres */}
            <div style={styles.contentWrapper}>
              <div style={styles.feedWrapper}>
                <WorkplaceFeed
                  contextType="collab"
                  contextId={selectedCollab.id}
                  token={token}
                  currentUser={currentUser}
                  members={members}
                  accentColor={selectedCollab.color || '#F97316'}
                  emptyStateMessage="Aucune publication dans cette collaboration"
                />
              </div>

              {/* Panel Membres */}
              <AnimatePresence>
                {showMembersPanel && (
                  <motion.aside
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 300, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    style={styles.membersPanel}
                  >
                    <div style={styles.membersPanelHeader}>
                      <h3>Membres ({members.length})</h3>
                      <button 
                        style={styles.closeButton}
                        onClick={() => setShowMembersPanel(false)}
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div style={styles.membersList}>
                      {members.map(member => (
                        <div key={member.id || member.user_id} style={styles.memberItem}>
                          <div style={styles.memberAvatar}>
                            {member.avatar ? (
                              <img src={member.avatar} alt="" style={styles.memberAvatarImg} />
                            ) : (
                              member.name?.charAt(0).toUpperCase() || '?'
                            )}
                          </div>
                          <div style={styles.memberInfo}>
                            <div style={styles.memberName}>
                              {member.name}
                              {member.is_external && (
                                <span style={styles.externalBadge}>Externe</span>
                              )}
                            </div>
                            <div style={styles.memberRole}>
                              {member.role === 'admin' && <Crown size={12} />}
                              {member.role === 'observer' && <Eye size={12} />}
                              {member.role === 'admin' ? 'Admin' : member.role === 'observer' ? 'Observateur' : 'Membre'}
                            </div>
                          </div>
                          {member.status === 'pending' && (
                            <span style={styles.pendingBadge}>En attente</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.aside>
                )}
              </AnimatePresence>
            </div>
          </>
        ) : (
          <div style={styles.noCollabSelected}>
            <Users size={60} style={{ color: '#CBD5E1', marginBottom: 16 }} />
            <h2>Sélectionne une collaboration</h2>
            <p>Ou crée-en une nouvelle pour commencer</p>
          </div>
        )}
      </main>

      {/* MODAL - Créer Collab */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateCollabModal 
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreateCollab}
          />
        )}
      </AnimatePresence>

      {/* MODAL - Inviter */}
      <AnimatePresence>
        {showInviteModal && selectedCollab && (
          <InviteModal 
            collab={selectedCollab}
            onClose={() => setShowInviteModal(false)}
            onInvite={handleInvite}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODAL - Créer une Collab
// ══════════════════════════════════════════════════════════════════════════════

function CreateCollabModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#F97316');

  const colors = ['#F97316', '#3B82F6', '#22C55E', '#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#64748B'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onCreate(name, description, color);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={styles.modalOverlay}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={styles.modal}
        onClick={e => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <h2>Créer une Collab</h2>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nom de la collaboration *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Projet Villa Dupont"
              style={styles.input}
              autoFocus
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Décris brièvement cette collaboration..."
              style={styles.textarea}
              rows={3}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Couleur</label>
            <div style={styles.colorPicker}>
              {colors.map(c => (
                <button
                  key={c}
                  type="button"
                  style={{
                    ...styles.colorOption,
                    background: c,
                    ...(color === c ? { ring: '3px solid', boxShadow: `0 0 0 3px ${c}40` } : {})
                  }}
                  onClick={() => setColor(c)}
                >
                  {color === c && <Check size={16} color="white" />}
                </button>
              ))}
            </div>
          </div>
          <div style={styles.modalActions}>
            <button type="button" style={styles.cancelButton} onClick={onClose}>
              Annuler
            </button>
            <button type="submit" style={styles.submitButton} disabled={!name.trim()}>
              Créer la Collab
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODAL - Inviter des membres
// ══════════════════════════════════════════════════════════════════════════════

function InviteModal({ collab, onClose, onInvite }) {
  const [tab, setTab] = useState('external'); // 'internal' | 'external'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('member');
  const [message, setMessage] = useState('');

  const handleInviteExternal = (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;
    onInvite('external', { email, name, role, message });
    setEmail('');
    setName('');
    setMessage('');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={styles.modalOverlay}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{ ...styles.modal, maxWidth: 500 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <h2>Inviter dans "{collab.name}"</h2>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(tab === 'external' ? styles.tabActive : {}) }}
            onClick={() => setTab('external')}
          >
            <Mail size={16} />
            Externe (Email)
          </button>
          <button
            style={{ ...styles.tab, ...(tab === 'internal' ? styles.tabActive : {}) }}
            onClick={() => setTab('internal')}
          >
            <Users size={16} />
            Équipe interne
          </button>
        </div>

        {tab === 'external' ? (
          <form onSubmit={handleInviteExternal}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="client@exemple.com"
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Nom *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Jean Dupont"
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Rôle</label>
              <select 
                value={role} 
                onChange={e => setRole(e.target.value)}
                style={styles.select}
              >
                <option value="member">Membre (peut publier)</option>
                <option value="observer">Observateur (lecture seule)</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Message personnalisé (optionnel)</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Un petit mot pour l'invité..."
                style={styles.textarea}
                rows={2}
              />
            </div>
            <div style={styles.infoBox}>
              <Mail size={16} />
              <span>Un email d'invitation sera envoyé avec un lien d'accès sécurisé.</span>
            </div>
            <div style={styles.modalActions}>
              <button type="button" style={styles.cancelButton} onClick={onClose}>
                Annuler
              </button>
              <button 
                type="submit" 
                style={styles.submitButton} 
                disabled={!email.trim() || !name.trim()}
              >
                <Send size={16} />
                Envoyer l'invitation
              </button>
            </div>
          </form>
        ) : (
          <div style={styles.internalTab}>
            <p style={styles.comingSoon}>
              La sélection depuis ton équipe arrive bientôt !<br />
              En attendant, utilise l'invitation par email.
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════════════════════════

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    background: '#F1F5F9',
    fontFamily: "'DM Sans', sans-serif",
  },
  sidebar: {
    width: 280,
    background: '#FFFFFF',
    borderRight: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 16px',
    borderBottom: '1px solid #E2E8F0',
    gap: 8,
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: '#F8FAFC',
    color: '#64748B',
    border: '1px solid #E2E8F0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sidebarTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 16,
    fontWeight: 700,
    color: '#1E293B',
    margin: 0,
    flex: 1,
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: '#F97316',
    color: '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    margin: '12px 16px',
    background: '#F8FAFC',
    borderRadius: 8,
    border: '1px solid #E2E8F0',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: 14,
    color: '#1E293B',
  },
  collabList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px',
  },
  collabItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  collabItemActive: {
    background: '#FFF7ED',
  },
  collabAvatar: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: 18,
  },
  collabInfo: {
    flex: 1,
    minWidth: 0,
  },
  collabName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1E293B',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  collabMeta: {
    fontSize: 12,
    color: '#64748B',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    color: '#64748B',
  },
  createFirstButton: {
    marginTop: 16,
    padding: '10px 20px',
    background: '#F97316',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    cursor: 'pointer',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  collabHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    background: '#FFFFFF',
    borderBottom: '1px solid #E2E8F0',
  },
  collabHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  collabAvatarLarge: {
    width: 56,
    height: 56,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: 24,
  },
  collabHeaderTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1E293B',
    margin: 0,
  },
  collabHeaderDesc: {
    fontSize: 14,
    color: '#64748B',
    margin: '4px 0 0',
  },
  collabHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  headerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    color: '#475569',
    cursor: 'pointer',
  },
  headerButtonPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    background: '#F97316',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: '#FFFFFF',
    cursor: 'pointer',
  },
  headerButtonIcon: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    color: '#475569',
    cursor: 'pointer',
  },
  contentWrapper: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  feedWrapper: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  membersPanel: {
    background: '#FFFFFF',
    borderLeft: '1px solid #E2E8F0',
    overflow: 'hidden',
  },
  membersPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid #E2E8F0',
  },
  closeButton: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    color: '#64748B',
  },
  membersList: {
    padding: '12px',
    overflowY: 'auto',
  },
  memberItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px',
    borderRadius: 8,
  },
  memberAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    color: '#64748B',
    overflow: 'hidden',
  },
  memberAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1E293B',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  memberRole: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: '#64748B',
  },
  externalBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: '#F97316',
    background: '#FFF7ED',
    padding: '2px 6px',
    borderRadius: 4,
  },
  pendingBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: '#64748B',
    background: '#F1F5F9',
    padding: '2px 6px',
    borderRadius: 4,
  },
  noCollabSelected: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748B',
    textAlign: 'center',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    maxWidth: 440,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #E2E8F0',
  },
  formGroup: {
    padding: '0 24px',
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#475569',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    background: '#FFFFFF',
    boxSizing: 'border-box',
  },
  colorPicker: {
    display: 'flex',
    gap: 8,
  },
  colorOption: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '20px 24px',
    borderTop: '1px solid #E2E8F0',
  },
  cancelButton: {
    padding: '10px 20px',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    color: '#475569',
    cursor: 'pointer',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: '#F97316',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: '#FFFFFF',
    cursor: 'pointer',
  },
  tabs: {
    display: 'flex',
    gap: 8,
    padding: '16px 24px',
    borderBottom: '1px solid #E2E8F0',
  },
  tab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    color: '#64748B',
    cursor: 'pointer',
  },
  tabActive: {
    background: '#FFF7ED',
    borderColor: '#F97316',
    color: '#F97316',
  },
  infoBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '0 24px 16px',
    padding: '12px',
    background: '#F0F9FF',
    borderRadius: 8,
    fontSize: 12,
    color: '#0369A1',
  },
  internalTab: {
    padding: '40px 24px',
    textAlign: 'center',
  },
  comingSoon: {
    color: '#64748B',
    fontSize: 14,
    lineHeight: 1.6,
  },
};
